import * as Sentry from "@sentry/react";
import React from "react";
import { createRoot } from "react-dom/client";

import App from "./App";

import { initMocks } from "@/common/api";
import "@/common/api/fetch-interceptor"; // Initialize fetch interceptor
import { ErrorBoundary } from "@/common/components/ui/error-boundary";
import "./index.css";

// Initialize Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN as string,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 0.1,
  // Session Replay
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0,
  environment: (import.meta.env.VITE_SENTRY_ENV as string) ?? "development",
});

function render() {
  createRoot(document.getElementById("root")!).render(
    <Sentry.ErrorBoundary fallback={<ErrorBoundary />} showDialog>
      <App />
    </Sentry.ErrorBoundary>,
  );
}

// Initialize Yandex Metrika only in production
if (import.meta.env.PROD) {
  const METRIKA_ID = 106926771;
  const w = window as any;

  w.ym =
    w.ym ||
    function (...args: unknown[]) {
      (w.ym.a = w.ym.a || []).push(args);
    };
  w.ym.l = 1 * +new Date();

  const scriptSrc = `https://mc.yandex.ru/metrika/tag.js?id=${METRIKA_ID}`;
  const alreadyLoaded = Array.from(document.scripts).some((s) => s.src === scriptSrc);
  if (!alreadyLoaded) {
    const k = document.createElement("script");
    const a = document.getElementsByTagName("script")[0];
    k.async = true;
    k.src = scriptSrc;
    a.parentNode?.insertBefore(k, a);
  }

  w.ym(METRIKA_ID, "init", {
    ssr: true,
    webvisor: true,
    clickmap: true,
    ecommerce: "dataLayer",
    referrer: document.referrer,
    url: location.href,
    accurateTrackBounce: true,
    trackLinks: true,
  });

  // Fallback noscript pixel
  const noscript = document.createElement("noscript");
  const div = document.createElement("div");
  const img = document.createElement("img");
  img.src = `https://mc.yandex.ru/watch/${METRIKA_ID}`;
  img.style.cssText = "position:absolute; left:-9999px;";
  img.alt = "";
  div.appendChild(img);
  noscript.appendChild(div);
  document.head.appendChild(noscript);
}

// Initialize MSW in development
if (import.meta.env.DEV) {
  initMocks().then(() => {
    render();
  });
} else {
  render();
}
